import axios from "axios";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_API_URL || "/api/v1";

const client = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
  withCredentials: true, // sends httpOnly cookies automatically
});

// No Authorization header needed — browser sends httpOnly cookie automatically.
// Mobile clients use Authorization: Bearer header directly (unchanged).

let refreshPromise = null;

function getErrorMessage(error) {
  const data = error.response?.data;
  if (data?.error) return data.error;
  if (data?.message) return data.message;
  if (error.code === "ECONNABORTED") return "Request timed out. Please try again.";
  if (!error.response) return "Network error. Please check your connection.";
  const status = error.response?.status;
  if (status === 403) return "You don't have permission for this action.";
  if (status === 404) return "Requested resource not found.";
  if (status === 429) return "Too many requests. Please wait and try again.";
  if (status >= 500) return "Server error. Please try again later.";
  return "Something went wrong. Please try again.";
}

let _muted = false;

client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes("/auth/")
    ) {
      original._retry = true;

      try {
        if (!refreshPromise) {
          // Cookie is sent automatically — no need to pass refreshToken in body
          refreshPromise = axios
            .post(`${API_BASE}/auth/refresh-token`, {}, { withCredentials: true })
            .finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        // New cookie is now set by the server — retry the original request
        return client(original);
      } catch {
        window.dispatchEvent(new Event("edupulse:logout"));
        return Promise.reject(error);
      }
    }

    const skipToast = original?._silent
      || _muted
      || error.response?.status === 401
      || original?.url?.includes("/auth/");

    if (!skipToast) {
      toast.error(getErrorMessage(error));
    }

    return Promise.reject(error);
  }
);

export function muteToasts(muted) { _muted = muted; }
export function isToastMuted() { return _muted; }

// Token storage is now handled by httpOnly cookies (set by server).
// These helpers are kept for any legacy references but are no-ops for security.
export function saveTokens(_token, _refresh) { /* cookies set by server */ }
export function clearTokens() {
  // Call logout endpoint so server can clear cookies
  axios.post(`${API_BASE}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
}

export function getSavedUser() {
  try {
    return JSON.parse(localStorage.getItem("edupulse_user"));
  } catch {
    return null;
  }
}

export function saveUser(user) {
  // Store only non-sensitive profile info (no tokens)
  const { _id, email, role, name, schoolId } = user;
  localStorage.setItem("edupulse_user", JSON.stringify({ _id, email, role, name, schoolId }));
}

export default client;
